import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import lottie from "lottie-web";
import { QRCodeCanvas } from "qrcode.react";
import styles from "./SendConsentComplete.module.scss";
import {
  FiCopy,
  FiLink,
  FiAlertCircle,
  FiCheck,
  FiGrid,
  FiX,
} from "react-icons/fi";
import confetti from "canvas-confetti";

const SUCCESS_LOTTIE = "/Confetti.json";
const NO_EMAIL_LOTTIE = "/bear.json";

const normalizePatientNoteMarkdown = (value) => {
  return String(value || "")
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t]+(#{1,6})[ \t]+/g, "\n$1 ")
    .replace(/[ \t]+(?=[-*+]\s+\*\*[^*\n]+:\*\*)/g, "\n")
    .trim();
};

const escapeHtml = (value) => {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const formatInlineMarkdown = (value) => {
  return escapeHtml(value)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/__(.+?)__/g, "<strong>$1</strong>");
};

const patientNoteToPlainText = (markdown) => {
  return normalizePatientNoteMarkdown(markdown)
    .split("\n")
    .map((line) =>
      line
        .replace(/^\s*#{1,6}\s+/, "")
        .replace(/^\s*[-*+]\s+/, "â€¢ ")
        .replace(/^\s*(\d+)[.)]\s+/, "$1. ")
        .replace(/\*\*(.+?)\*\*/g, "$1")
        .replace(/__(.+?)__/g, "$1")
        .trimEnd(),
    )
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const patientNoteToHtml = (markdown) => {
  const lines = normalizePatientNoteMarkdown(markdown).split("\n");
  const html = [];
  let activeList = null;

  const closeList = () => {
    if (!activeList) return;

    html.push(`</${activeList}>`);
    activeList = null;
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trim();

    if (!line) {
      closeList();
      html.push('<div style="height:8px;line-height:8px;">&nbsp;</div>');
      return;
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);

    if (headingMatch) {
      closeList();
      const level = Math.min(headingMatch[1].length, 4);
      const fontSize =
        level === 1 ? 24 : level === 2 ? 22 : level === 3 ? 20 : 18;

      html.push(
        `<h${level} style="margin:16px 0 8px;font-size:${fontSize}px;line-height:1.3;font-weight:700;color:#111827;">${formatInlineMarkdown(
          headingMatch[2],
        )}</h${level}>`,
      );
      return;
    }

    const unorderedMatch = line.match(/^[-*+]\s+(.+)$/);
    const orderedMatch = line.match(/^\d+[.)]\s+(.+)$/);

    if (unorderedMatch || orderedMatch) {
      const nextList = unorderedMatch ? "ul" : "ol";

      if (activeList !== nextList) {
        closeList();
        html.push(
          `<${nextList} style="margin:0 0 12px 22px;padding:0;color:#1f2937;">`,
        );
        activeList = nextList;
      }

      html.push(
        `<li style="margin:0 0 7px;line-height:1.55;">${formatInlineMarkdown(
          (unorderedMatch || orderedMatch)[1],
        )}</li>`,
      );
      return;
    }

    closeList();
    html.push(
      `<p style="margin:0 0 10px;line-height:1.55;color:#1f2937;">${formatInlineMarkdown(
        line,
      )}</p>`,
    );
  });

  closeList();

  return `<div style="font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.55;color:#1f2937;">${html.join(
    "",
  )}</div>`;
};

const copyRichPatientNote = async (markdown) => {
  const plainText = patientNoteToPlainText(markdown);
  const html = patientNoteToHtml(markdown);

  if (navigator.clipboard?.write && typeof ClipboardItem !== "undefined") {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([plainText], { type: "text/plain" }),
        }),
      ]);
      return;
    } catch (error) {
      console.warn("Rich clipboard copy failed; using fallback:", error);
    }
  }

  const copyContainer = document.createElement("div");
  copyContainer.innerHTML = html;
  copyContainer.setAttribute("contenteditable", "true");
  copyContainer.style.position = "fixed";
  copyContainer.style.left = "-9999px";
  copyContainer.style.top = "0";
  copyContainer.style.opacity = "0";
  document.body.appendChild(copyContainer);

  const selection = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(copyContainer);
  selection.removeAllRanges();
  selection.addRange(range);

  let copied = false;

  try {
    copied = document.execCommand("copy");
  } finally {
    selection.removeAllRanges();
    document.body.removeChild(copyContainer);
  }

  if (!copied) {
    await navigator.clipboard.writeText(plainText);
  }
};

function LottieAnimation({ src, className, loop = true }) {
  const containerRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !src) return undefined;

    animationRef.current = lottie.loadAnimation({
      container: containerRef.current,
      renderer: "svg",
      loop,
      autoplay: true,
      path: src,
      rendererSettings: {
        preserveAspectRatio: "xMidYMid meet",
      },
    });

    return () => {
      if (animationRef.current) {
        animationRef.current.destroy();
        animationRef.current = null;
      }
    };
  }, [src, loop]);

  return <div ref={containerRef} className={className} />;
}

export default function SendConsentComplete() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [treatmentData, setTreatmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedPatientNote, setCopiedPatientNote] = useState(false);
  const [showQr, setShowQr] = useState(false);

  useEffect(() => {
    const loadConsentBundle = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(`consent_bundle/${id}`);
        setTreatmentData(res.data);
      } catch (err) {
        console.error("Error loading consent bundle:", err);
        setTreatmentData(null);
      } finally {
        setLoading(false);
      }
    };

    loadConsentBundle();
  }, [id]);

  const patientEmail = useMemo(() => {
    return (
      treatmentData?.patient?.email_address?.trim() ||
      treatmentData?.patient?.email?.trim() ||
      treatmentData?.email?.trim() ||
      ""
    );
  }, [treatmentData]);

  const patientNote = useMemo(() => {
    return (
      treatmentData?.notes?.trim() ||
      treatmentData?.patient_note?.trim() ||
      treatmentData?.patientNote?.trim() ||
      ""
    );
  }, [treatmentData]);

  const hasEmail = Boolean(patientEmail);
  const hasPatientNote = Boolean(patientNote);

  useEffect(() => {
    if (!hasEmail || loading) return undefined;

    const duration = 3000;
    const end = Date.now() + duration;
    let stopped = false;

    const colors = [
      "#ff595e",
      "#ffca3a",
      "#8ac926",
      "#1982c4",
      "#6a4c93",
      "#ff9f1c",
      "#4ecdc4",
    ];

    const frame = () => {
      if (stopped) return;

      confetti({
        particleCount: 10,
        startVelocity: 42,
        spread: 75,
        origin: { x: 0.12, y: Math.random() * 0.45 + 0.15 },
        colors,
      });

      confetti({
        particleCount: 10,
        startVelocity: 42,
        spread: 75,
        origin: { x: 0.88, y: Math.random() * 0.45 + 0.15 },
        colors,
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    frame();

    return () => {
      stopped = true;
    };
  }, [hasEmail, loading]);

  const patientName = useMemo(() => {
    const first =
      treatmentData?.patient?.first_name?.trim() ||
      treatmentData?.patient?.firstname?.trim() ||
      "";

    const last =
      treatmentData?.patient?.last_name?.trim() ||
      treatmentData?.patient?.lastname?.trim() ||
      "";

    return `${first} ${last}`.trim();
  }, [treatmentData]);

  const patientBaseUrl = (process.env.REACT_APP_PATIENT_URL || "").replace(
    /\/$/,
    "",
  );

  const link = id ? `${patientBaseUrl}/${id}` : patientBaseUrl;

  const copyToClipboard = async (text, setCopiedState, errorMessage) => {
    try {
      await navigator.clipboard.writeText(text);

      setCopiedState(true);

      setTimeout(() => {
        setCopiedState(false);
      }, 1800);
    } catch (error) {
      console.error("Failed to copy:", error);
      setCopiedState(false);
      alert(errorMessage);
    }
  };

  const handleCopyLink = async () => {
    await copyToClipboard(
      link,
      setCopiedLink,
      "Could not copy the link. Please copy it manually.",
    );
  };

  const handleCopyPatientNote = async () => {
    if (!hasPatientNote) {
      alert("No patient note is available for this consent bundle.");
      return;
    }

    try {
      await copyRichPatientNote(patientNote);
      setCopiedPatientNote(true);

      setTimeout(() => {
        setCopiedPatientNote(false);
      }, 1800);
    } catch (error) {
      console.error("Failed to copy patient note:", error);
      setCopiedPatientNote(false);
      alert("Could not copy the patient note. Please try again.");
    }
  };

  const handleQrToggle = () => {
    setShowQr((prev) => !prev);
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.loadingCard}>
            <div className={styles.loader} />
            <p>Loading consent details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (hasEmail) {
    return (
      <div className={styles.page}>
        <div className={styles.successOldContainer}>
          <div className={styles.successOldIllustration}>
            <LottieAnimation
              src={SUCCESS_LOTTIE}
              className={styles.successOldLottie}
              loop={true}
            />
          </div>

          <h1 className={styles.successOldTitle}>
            Well done! Your consent form has been emailed
          </h1>

          <div className={styles.successOldCard}>
            <h2>Email sent successfully</h2>

            <p>
              Weâ€™ve provided your patient with a secure link to view and
              complete their consent documents. If youâ€™d like to resend the form
              manually via email or text, simply copy the link below.
            </p>

            <p className={styles.successOldLink}>{link}</p>

            <div
              className={styles.successCopyActions}
              style={{
                display: "flex",
                flexWrap: "wrap",
                columnGap: "12px",
                rowGap: "12px",
              }}
            >
              <button
                className={styles.successOldCopyBtn}
                onClick={handleCopyLink}
                type="button"
              >
                {copiedLink ? <FiCheck size={14} /> : <FiCopy size={14} />}
                {copiedLink ? "Copied" : "Copy link"}
              </button>

              <button
                className={styles.successOldCopyBtn}
                onClick={handleCopyPatientNote}
                type="button"
                disabled={!hasPatientNote}
                title={
                  hasPatientNote
                    ? "Copy patient note"
                    : "No patient note available"
                }
              >
                {copiedPatientNote ? (
                  <FiCheck size={14} />
                ) : (
                  <FiCopy size={14} />
                )}
                {copiedPatientNote
                  ? "Patient note copied"
                  : "Copy patient note"}
              </button>
            </div>
          </div>

          <div className={styles.actions}>
            <button
              className={styles.primaryBtn}
              onClick={() => navigate(`/summary-new/${id}`)}
              type="button"
            >
              View summary
            </button>

            <button
              className={styles.secondaryBtn}
              onClick={() => navigate("/dashboard")}
              type="button"
            >
              Go to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div
          className={`${styles.animationWrapper} ${styles.noEmailAnimation}`}
        >
          <LottieAnimation
            src={NO_EMAIL_LOTTIE}
            className={styles.lottieAnimation}
            loop={true}
          />
        </div>

        <h1 className={styles.title}>No email address</h1>

        <div className={`${styles.card} ${styles.warningCard}`}>
          <div className={styles.statusHeader}>
            <div className={`${styles.iconBubble} ${styles.warningBubble}`}>
              <FiAlertCircle />
            </div>

            <div>
              <h2>Share manually</h2>

              {patientName && (
                <p className={styles.patientName}>
                  Patient: <strong>{patientName}</strong>
                </p>
              )}
            </div>
          </div>

          <p className={styles.message}>
            This patient has no email saved. Copy the link or use the QR code to
            share manually.
          </p>

          <div className={styles.linkBox}>
            <FiLink className={styles.linkIcon} />
            <p className={styles.link}>{link}</p>
          </div>

          <div
            className={styles.shareActions}
            style={{
              display: "flex",
              flexWrap: "wrap",
              columnGap: "12px",
              rowGap: "12px",
            }}
          >
            <button
              className={styles.copyBtn}
              onClick={handleCopyLink}
              type="button"
            >
              {copiedLink ? <FiCheck size={16} /> : <FiCopy size={16} />}
              {copiedLink ? "Copied" : "Copy link"}
            </button>

            <button
              className={styles.copyBtn}
              onClick={handleCopyPatientNote}
              type="button"
              disabled={!hasPatientNote}
              title={
                hasPatientNote
                  ? "Copy patient note"
                  : "No patient note available"
              }
            >
              {copiedPatientNote ? <FiCheck size={16} /> : <FiCopy size={16} />}
              {copiedPatientNote ? "Patient note copied" : "Copy patient note"}
            </button>

            <button
              className={styles.qrBtn}
              onClick={handleQrToggle}
              type="button"
            >
              {showQr ? <FiX size={16} /> : <FiGrid size={16} />}
              {showQr ? "Hide QR" : "Show QR"}
            </button>
          </div>

          {showQr && (
            <div className={styles.qrBox}>
              <QRCodeCanvas value={link} size={150} level="H" includeMargin />
              <p>Scan to open consent form</p>
            </div>
          )}
        </div>

        <div className={styles.actions}>
          <button
            className={styles.primaryBtn}
            onClick={() => navigate(`/summary-new/${id}`)}
            type="button"
          >
            View summary
          </button>

          <button
            className={styles.secondaryBtn}
            onClick={() => navigate("/dashboard")}
            type="button"
          >
            Go to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
